import React, { useState } from "react";
import Layout from "../components/Layout";
import { PageHero } from "../components/PageHero";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiError } from "../lib/api";
import { Mail, Phone, MapPin, Linkedin, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", topic: "executive", message: "" });
  const [rgpd, setRgpd] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", company: "", topic: "executive", message: "" });
      setRgpd(false);
    } catch (err) {
      toast.error(formatApiError(err) || t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <PageHero eyebrow={t.contact.eyebrow} title={t.contact.title} />

      <section className="px-6 md:px-10 max-w-[1400px] mx-auto pb-28">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5">
            <p className="font-serif text-2xl md:text-3xl leading-snug font-light text-ink mb-10">{t.contact.intro}</p>
            <ul className="space-y-5 text-ink/80">
              <li className="flex items-start gap-4"><MapPin size={18} className="text-brand mt-1" /><span>{t.contact.address}</span></li>
              <li className="flex items-center gap-4"><Phone size={18} className="text-brand" /><a href="tel:+33652303000" className="link-underline">{t.contact.phone_val}</a></li>
              <li className="flex items-center gap-4"><Mail size={18} className="text-brand" /><a href="mailto:contact@audace-perspectives.com" className="link-underline">contact@audace-perspectives.com</a></li>
              <li className="flex items-center gap-4"><Linkedin size={18} className="text-brand" /><a href="https://www.linkedin.com/company/audaceperspectives/" target="_blank" rel="noopener noreferrer" className="link-underline">LinkedIn</a></li>
            </ul>

            <div className="hairline bg-black my-12"></div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40 mb-3">— Confidentialité</div>
            <p className="text-ink/65 text-sm leading-relaxed">Toutes les informations transmises restent strictement confidentielles, conformément au code éthique de la SF Coach.</p>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="col-span-12 md:col-span-7 bg-white border border-black/10 p-8 md:p-12">
            {sent && (
              <div className="mb-6 flex items-center gap-3 p-4 bg-brand/10 border border-brand/30" data-testid="contact-success">
                <Check size={18} className="text-brand" />
                <span className="text-sm text-ink">{t.contact.success}</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label={t.contact.name} required>
                <input data-testid="contact-name" required value={form.name} onChange={setF("name")} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand transition-colors" />
              </Field>
              <Field label={t.contact.email} required>
                <input type="email" data-testid="contact-email" required value={form.email} onChange={setF("email")} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand transition-colors" />
              </Field>
              <Field label={t.contact.phone}>
                <input data-testid="contact-phone" value={form.phone} onChange={setF("phone")} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand transition-colors" />
              </Field>
              <Field label={t.contact.company}>
                <input data-testid="contact-company" value={form.company} onChange={setF("company")} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand transition-colors" />
              </Field>
              <Field label={t.contact.topic} className="md:col-span-2">
                <select data-testid="contact-topic" value={form.topic} onChange={setF("topic")} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand transition-colors">
                  <option value="executive">{t.contact.topics.executive}</option>
                  <option value="team">{t.contact.topics.team}</option>
                  <option value="formation">{t.contact.topics.formation}</option>
                  <option value="outplacement">{t.contact.topics.outplacement}</option>
                  <option value="partenariat">{t.contact.topics.partenariat}</option>
                  <option value="pse">{t.contact.topics.pse}</option>
                  <option value="other">{t.contact.topics.other}</option>
                </select>
              </Field>
              <Field label={t.contact.message} required className="md:col-span-2">
                <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={setF("message")} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand transition-colors resize-none"></textarea>
              </Field>
            </div>

            <div className="mt-8 flex items-start gap-3" data-testid="contact-rgpd-row">
              <input
                id="contact-rgpd"
                data-testid="contact-rgpd"
                type="checkbox"
                required
                checked={rgpd}
                onChange={(e) => setRgpd(e.target.checked)}
                className="mt-1 h-4 w-4 accent-brand"
              />
              <label htmlFor="contact-rgpd" className="text-xs text-ink/70 leading-relaxed">
                {lang === "fr"
                  ? "J'accepte que les informations transmises soient traitées par Audace & Perspectives dans le cadre de ma demande, conformément à la "
                  : "I consent to Audace & Perspectives processing the information submitted for the purpose of my request, in accordance with the "}
                <a href="/rgpd" target="_blank" rel="noopener noreferrer" className="text-brand link-underline">
                  {lang === "fr" ? "politique de confidentialité" : "privacy policy"}
                </a>
                {lang === "fr" ? "." : "."}
              </label>
            </div>

            <button data-testid="contact-submit" type="submit" disabled={sending || !rgpd} className="mt-8 inline-flex items-center gap-5 bg-brand hover:bg-brandDark disabled:opacity-40 disabled:cursor-not-allowed text-white px-8 py-5 transition-colors">
              <span className="text-sm tracking-[0.16em] uppercase font-mono">{sending ? "..." : t.contact.send}</span>
              <ArrowUpRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">
        {label}{required && <span className="text-brand"> *</span>}
      </label>
      {children}
    </div>
  );
}
